import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import i18n from '~/components/i18n'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { useDebounce } from 'use-lodash-debounce'
import { Dropdown, Input, Popup } from 'semantic-ui-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { DateInput } from 'semantic-ui-calendar-react'
import CustomIcon from '~/components/CustomIcon'
import { FilterWrapper } from '~/pages/Dairy/DairyFactory/Farmers/style'
import { FarmersService } from '~/api/services/farmers/index'
import { handleErrors } from '~/utils/helpers'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Email'),
    key: 'email'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'email-status'
  },
  {
    name: i18n.t('Type'),
    type: 'email-type',
    key: 'type'
  },
  {
    name: i18n.t('Subject'),
    key: 'subject'
  },
  {
    name: i18n.t('Sent date'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'sentDate',
    sorted: 'desc'
  },
  {
    name: i18n.t('Delivered date'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'deliveredDate',
    sorted: 'desc'
  },
  {
    name: i18n.t('Opened date'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'openedDate',
    sorted: 'desc'
  }
]

const Emails: React.FC = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const [dataFiltered, setDataFiltered] = useState({
    status: '',
    type: '',
    sentDateFrom: '',
    sentDateTo: '',
    search: '',
    column: '',
    sort: '',
    page: 0,
    rows: 10
  })
  const [search, setSearch] = useState('')
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [emailsOptions, setEmailsOptions] = useState(null)
  const [emailsData, setEmailsData] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const debouncedValue = useDebounce(search, 300)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  useEffect(() => {
    fetchOptions()
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  async function fetchOptions() {
    setIsBusy(true)
    try {
      const response = await FarmersService.getManagerEmailsOptions(id)
      setEmailsOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function fetchData() {
    setIsFetching(true)
    try {
      const response = await FarmersService.getManagerEmails(id, {
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
        sentDateFrom: dataFiltered.sentDateFrom !== '' ? parseDate(dataFiltered.sentDateFrom) : '',
        sentDateTo: dataFiltered.sentDateTo !== '' ? parseDate(dataFiltered.sentDateTo) : ''
      })
      setEmailsData(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsFetching(false)
    }
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  function renderEmailStatusColumn(value) {
    switch (value) {
      case 'Delivery':
        return <Popup content={t('Delivered')} trigger={<CustomIcon name='envelope outline' size='large' />} />
      case 'Open':
        return <Popup content={t('Opened')} trigger={<CustomIcon name='envelope open' size='large' />} />
      case 'Send':
        return <Popup content={t('Send')} trigger={<CustomIcon name='envelope' size='large' />} />
      default:
        return
    }
  }

  function renderEmailTypeColumn(value, row) {
    switch (value) {
      case 'email':
        return <Popup content={row?.typeLabel || t('Email')} trigger={<CustomIcon name='envelope square' size='large' />} />
      case 'newsletter':
        return <Popup content={row?.typeLabel || t('Newsletter')} trigger={<CustomIcon className='icon-newsletter-2' size='large' />} />
      default:
        return
    }
  }

  function renderColumn(type, value, row) {
    switch (type) {
      case 'email-status': return renderEmailStatusColumn(value)
      case 'email-type': return renderEmailTypeColumn(value, row)
      default: return
    }
  }

  const handleKeyPress = (event) => { event.preventDefault() }

  function renderFilters() {
    return <FilterWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        clearable
        selection
        selectOnBlur={false}
        options={emailsOptions?.types?.map(o => ({ value: o.key, text: o.label })) || []}
        placeholder={t('Select type...')}
        onChange={(_, data) => setDataFiltered({ ...dataFiltered, type: data.value as string, page: 0 })}
      />
      <Dropdown
        selection
        clearable
        selectOnBlur={false}
        options={emailsOptions?.statuses?.map(o => ({ value: o.key, text: o.label })) || []}
        placeholder={t('Select status...')}
        onChange={(_, data) => setDataFiltered({ ...dataFiltered, status: data.value as string, page: 0 })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.sentDateFrom}
        onKeyPress={handleKeyPress}
        iconPosition='left'
        onChange={(event, { value }) => setDataFiltered({ ...dataFiltered, sentDateFrom: value, page: 0 })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date to')}
        closable
        clearIcon={'close'}
        clearable
        onKeyPress={handleKeyPress}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        minDate={dataFiltered.sentDateFrom}
        value={dataFiltered.sentDateTo}
        iconPosition='left'
        onChange={(event, { value }) => setDataFiltered({ ...dataFiltered, sentDateTo: value, page: 0 })}
      />
    </FilterWrapper>
  }

  const { filtered, rows } = emailsData ? emailsData : { filtered: 0, rows: [] }

  return <Layout
    title={''}
    childContainerSize={'auto'}
    filters={renderFilters()}
    useSpinner
    isBusy={isBusy}
  >
    <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, headers) => {
        setTableColumnHeaders(headers)
        setDataFiltered(data)
      }}
      renderColumn={(type, value, row) => renderColumn(type, value, row)}
      rows={!isFetching ? rows || [] : []}
      animated
      upward
      isFetchingData={isFetching}
      total={filtered}
    />
  </Layout>
}

export default Emails
